import { useState, useEffect } from 'react';
import BuilderDashboard  from './pages/BuilderDashboard';
import BuilderProjects   from './pages/BuilderProjects';
import BuilderMaterials  from './pages/BuilderMaterials';
import BuilderPurchases  from './pages/BuilderPurchases';
import BuilderInventory  from './pages/BuilderInventory';
import BuilderIssues     from './pages/BuilderIssues';
import BuilderWork       from './pages/BuilderWork';
import BuilderEmployees  from './pages/BuilderEmployees';
import BuilderReports    from './pages/BuilderReports';
import BuilderPayments   from './pages/BuilderPayments';
import BuilderSettings   from './pages/BuilderSettings';
import BuilderDocuments  from './pages/BuilderDocuments';

const NAV = [
  { key: 'dashboard',  icon: '🏠', label: 'Dashboard' },
  { key: 'projects',   icon: '🏗️', label: 'Projects' },
  { key: 'materials',  icon: '📦', label: 'Materials' },
  { key: 'purchases',  icon: '🛒', label: 'Purchases' },
  { key: 'inventory',  icon: '🏭', label: 'Inventory' },
  { key: 'issues',     icon: '🔄', label: 'Issue Materials' },
  { key: 'work',       icon: '👷', label: 'Work Completion' },
  { key: 'employees',  icon: '👥', label: 'Employees' },
  { key: 'payments',   icon: '💰', label: 'Payments' },
  { key: 'reports',    icon: '📊', label: 'Reports' },
  { key: 'settings',   icon: '⚙️', label: 'Settings' },
  { key: 'documents',  icon: '📄', label: 'Documents' },
];

function pages(navigate, user) {
  return {
    dashboard: <BuilderDashboard navigate={navigate} />,
    projects:  <BuilderProjects user={user} />,
    materials: <BuilderMaterials user={user} />,
    purchases: <BuilderPurchases user={user} />,
    inventory: <BuilderInventory />,
    issues:    <BuilderIssues user={user} />,
    work:      <BuilderWork user={user} />,
    employees: <BuilderEmployees user={user} />,
    payments:  <BuilderPayments user={user} />,
    reports:   <BuilderReports />,
    settings:  <BuilderSettings />,
  };
}

const TITLES = {
  dashboard: '🏠 Dashboard',
  projects:  '🏗️ Projects',
  materials: '📦 Material Catalog',
  purchases: '🛒 Purchases',
  inventory: '🏭 Inventory',
  issues:    '🔄 Issue Materials',
  work:      '👷 Work Completion',
  employees: '👥 Employees',
  payments:  '💰 Payments',
  reports:   '📊 Reports',
  settings:  '⚙️ Settings',
  documents: '📄 Documents',
};

const DESKTOP_BP = 1100;
const TABLET_BP  = 768;

function initials(name) {
  if (!name) return 'B';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function BuilderApp({ user, handleLogout }) {
  const [active,     setActive]     = useState('dashboard');
  const [collapsed,  setCollapsed]  = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile,   setIsMobile]   = useState(window.innerWidth < TABLET_BP);
  const [isTablet,   setIsTablet]   = useState(window.innerWidth < DESKTOP_BP);
  const [theme,      setTheme]      = useState(() => localStorage.getItem('smabizz-theme') || 'light');

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < TABLET_BP);
      setIsTablet(w < DESKTOP_BP);
      if (w >= DESKTOP_BP) setDrawerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const toggleTheme = () => setTheme(t => {
    const next = t === 'light' ? 'dark' : 'light';
    localStorage.setItem('smabizz-theme', next);
    return next;
  });

  const handleNav = (key) => {
    setActive(key);
    setDrawerOpen(false);
  };

  const displayName = user?.username || 'Builder';

  // Sidebar visibility logic:
  // Desktop: fixed, collapses to icon-only
  // Tablet: fixed at icon-only width, drawer opens full panel
  // Mobile: completely off-screen, drawer slides in
  const sidebarClasses = [
    'sidebar',
    (isMobile || isTablet) && !drawerOpen ? 'collapsed' : '',
    drawerOpen               ? 'drawer-open'  : '',
    !isMobile && !isTablet && collapsed && !drawerOpen ? 'collapsed' : '',
  ].filter(Boolean).join(' ');

  const mainClasses = [
    'main',
    isMobile               ? 'bld-main-mobile'  : '',
    isTablet && !isMobile  ? 'bld-main-tablet'  : '',
    !isTablet && collapsed ? 'expanded'          : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="app" data-theme={theme}>

      {/* ── Backdrop (mobile/tablet drawer) ─────────────────── */}
      {drawerOpen && (
        <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside className={sidebarClasses}
        style={{ background: 'linear-gradient(180deg,#1a1f2e 0%,#2d3748 100%)' }}
        onMouseEnter={() => { if (!isMobile && !isTablet && collapsed) setCollapsed(false); }}
        onMouseLeave={() => { if (!isMobile && !isTablet) setCollapsed(true); }}>

        {/* Brand */}
        <div className="sidebar-brand" style={{ background: '#0f1623' }}>
          <div className="brand-icon"
            onClick={() => isMobile || isTablet ? setDrawerOpen(d => !d) : setCollapsed(c => !c)}
            title={isMobile || isTablet ? (drawerOpen ? 'Close menu' : 'Open menu') : (collapsed ? 'Expand menu' : 'Collapse menu')}
            style={{ background: 'linear-gradient(135deg,#d69e2e,#f6ad55)', borderRadius: 8, flexShrink: 0,
              cursor: 'pointer', transition: 'opacity .15s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <span style={{ color: '#fff', fontSize: 16 }}>🪖</span>
          </div>
          <div className="brand-text" style={{ color: '#fff', flex: 1, minWidth: 0, overflow: 'hidden' }}>
            SmaBizZ<span style={{ color: '#d69e2e' }}>Build</span>
          </div>
          {/* Desktop collapse toggle */}
          {!isMobile && !isTablet && (
            <button className="sidebar-toggle"
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Expand menu' : 'Collapse menu'}>
              ☰
            </button>
          )}
          {/* Drawer close (tablet/mobile when open) */}
          {drawerOpen && (
            <button className="sidebar-toggle" onClick={() => setDrawerOpen(false)} title="Close menu"
              style={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
              ✕
            </button>
          )}
        </div>

        {/* Business subtitle — only when sidebar is expanded */}
        {(!collapsed || drawerOpen || isTablet && drawerOpen) && user?.businesstitle && (
          <div style={{ padding: '6px 16px', fontSize: 11, color: '#a0aec0', fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            borderBottom: '1px solid rgba(255,255,255,0.07)', background: '#0f1623' }}>
            {user.businesstitle}
          </div>
        )}

        {/* Prominent close button — shown inside the nav on mobile/tablet when drawer is open */}
        {(isMobile || isTablet) && drawerOpen && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <button onClick={() => setDrawerOpen(false)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8, color: '#e2e8f0', fontSize: 14, fontWeight: 600, padding: '8px 16px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>✕</span> Close Menu
            </button>
          </div>
        )}

        {/* Nav items */}
        <nav className="sidebar-nav" style={{ paddingTop: 8 }}>
          {NAV.map(item => (
            <div key={item.key}
              className={`nav-item${active === item.key ? ' active' : ''}`}
              onClick={() => handleNav(item.key)}
              title={item.label}>
              <span className="nav-icon" style={{ fontSize: 17, minWidth: 20, textAlign: 'center' }}>
                {item.icon}
              </span>
              <span className="nav-label" style={{ fontSize: 13 }}>{item.label}</span>
            </div>
          ))}

          <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 8 }}>
            <div className="nav-item" onClick={handleLogout}
              style={{ color: '#fc8181' }} title="Sign Out">
              <span className="nav-icon" style={{ fontSize: 17, minWidth: 20, textAlign: 'center' }}>🚪</span>
              <span className="nav-label" style={{ color: '#fc8181', fontSize: 13 }}>Sign Out</span>
            </div>
          </div>
        </nav>

        {/* Sidebar footer */}
        <div className="sidebar-footer" style={{ marginBottom: 52 }}>
          <div className="sidebar-user">
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#d69e2e,#f6ad55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
              {initials(displayName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
              <div style={{ fontSize: 11, color: '#a0aec0', textTransform: 'capitalize' }}>
                {user?.role?.replace('role_', '') || 'Builder'}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────────── */}
      <div className={mainClasses} style={
        isMobile ? { marginLeft: 0 }
        : isTablet ? { marginLeft: 64 }
        : { marginLeft: collapsed ? 64 : 240 }
      }>

        {/* ── Topbar — sticky at top:0 (no banner above) ─────── */}
        <div className="topbar bld-topbar"
          style={{ top: 0, position: 'sticky', zIndex: 50,
            background: 'linear-gradient(90deg,#1a1f2e,#2d3748)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>

          {/* Hamburger — always shown on mobile/tablet */}
          <button
            onClick={() => setDrawerOpen(d => !d)}
            title={drawerOpen ? 'Close menu' : 'Open menu'}
            style={{
              display: (isMobile || isTablet) ? 'flex' : 'none',
              alignItems: 'center', justifyContent: 'center',
              background: drawerOpen ? 'rgba(214,158,46,0.25)' : 'rgba(255,255,255,0.1)',
              border: `2px solid ${drawerOpen ? '#d69e2e' : 'rgba(255,255,255,0.35)'}`,
              borderRadius: 8, padding: '6px 12px', cursor: 'pointer', flexShrink: 0,
              color: '#e2e8f0', fontSize: 22, lineHeight: 1, fontWeight: 700,
            }}>
            {drawerOpen ? '✕' : '☰'}
          </button>

          {/* Left: page title */}
          <div style={{ color: '#a0aec0', fontSize: isMobile ? 13 : 14, fontWeight: 600, flexShrink: 0 }}>
            {TITLES[active]}
          </div>

          {/* Center: business name */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center',
            overflow: 'hidden', padding: '0 12px' }}>
            {user?.businesstitle && (
              <div style={{ fontSize: isMobile ? 13 : 16, fontWeight: 700, color: '#e2e8f0',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textAlign: 'center', letterSpacing: '.3px' }}>
                {user.businesstitle}
              </div>
            )}
          </div>

          <div className="topbar-right">
            <button className="topbar-btn" title={theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            {!isMobile && (
              <button className="topbar-btn" title="Sign Out" onClick={handleLogout}>
                🚪
              </button>
            )}
            <div className="avatar"
              style={{ background: 'linear-gradient(135deg,#d69e2e,#f6ad55)', color: '#fff' }}
              title={displayName}>
              {initials(displayName)}
            </div>
          </div>
        </div>

        {/* ── Page content ───────────────────────────────────── */}
        <div className="content" style={{ paddingBottom: 60 }}>
          {active === 'documents'
            ? <BuilderDocuments user={user} theme={theme} />
            : pages(handleNav, user)[active]}
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 900,
        background: '#0f1623', borderTop: '1px solid rgba(255,255,255,0.08)',
        textAlign: 'center', padding: '7px 16px' }}>
        <p style={{ color: '#718096', fontSize: 11, margin: 0, letterSpacing: '.2px' }}>
          &copy; {new Date().getFullYear()} SmaBizZ Construction Management &nbsp;|&nbsp;
          <a href="https://smabizz.com" style={{ color: '#d69e2e', textDecoration: 'none' }}>smabizz.com</a>
        </p>
      </footer>
    </div>
  );
}
